import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { useIntl } from 'react-intl'
import { Keyboard, NativeSyntheticEvent, Platform, Pressable, TextInputChangeEventData, ViewStyle } from 'react-native'

import { isString } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  hidePasswordButtonLabel,
  showPasswordButtonLabel,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import {
  isValidLength,
  isValidLowerCase,
  isValidNumber,
  isValidUpperCase,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import { PasswordPolicy, PasswordPolicyMet } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'

import { PasswordChecklist } from './PasswordChecklist'
import { AccessibilityRolesNative, IS_WEB } from '../../../constants'
import { EyeIcon } from '../../icons/EyeIcon'
import { InputField, InputFieldProps } from '../InputField'

export interface PasswordFieldProps extends Pick<
  InputFieldProps,
  'setInputRefs' | 'returnKeyType' | 'areAllInputRefsSet' | 'getReturnKeyType' | 'navigateToNewField'
> {
  name: string
  label?: string
  error?: string
  active?: boolean
  enablePasswordChecklist: boolean
  passwordPolicy?: PasswordPolicy
  onChange: (value: string) => void
  onBlur?: () => void
  onFocus?: () => void
  baseInputStyle?: ViewStyle
  inputContainerStyle?: ViewStyle
  labelContainerStyle?: ViewStyle
  showPasswordChecklistByDefault?: boolean
  hidePasswordChecklistOnBlur?: boolean
  autoComplete?: 'current-password' | 'new-password'
  inputAccessoryViewID?: string
  elevateChecklist?: boolean
}

const Container = styled.View<{ elevateChecklist?: boolean }>(({ elevateChecklist }) => ({
  width: '100%',
  ...(elevateChecklist && { zIndex: 1 }),
}))

export const PasswordField: FunctionComponent<PasswordFieldProps> = ({
  name,
  label,
  error,
  active,
  enablePasswordChecklist,
  passwordPolicy,
  onChange = () => {},
  onBlur = () => {},
  onFocus = () => {},
  baseInputStyle,
  inputContainerStyle,
  labelContainerStyle,
  showPasswordChecklistByDefault,
  hidePasswordChecklistOnBlur,
  autoComplete = 'current-password',
  inputAccessoryViewID,
  elevateChecklist,
  returnKeyType,
  areAllInputRefsSet,
  setInputRefs,
  navigateToNewField,
  getReturnKeyType,
}) => {
  const {
    breakpoints: { isMobileSized },
    colors,
  } = useTheme()
  const { formatMessage } = useIntl()

  const [showPassword, setShowPassword] = useState(false)
  const [shouldShowPasswordChecklist, setShouldShowPasswordChecklist] = useState(showPasswordChecklistByDefault)

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleKeyDown = (event: any) => {
    if (event?.key === 'Enter') {
      togglePasswordVisibility()
    }
  }

  const { minLength, minLowerCase, minUpperCase, minNumber } = passwordPolicy || {}
  const [passwordStrength, setPasswordStrength] = useState<PasswordPolicyMet>({
    minUpperCase: !passwordPolicy, // Init true to skip password validation
    minLowerCase: !passwordPolicy,
    minNumber: !passwordPolicy,
    minLength: !passwordPolicy,
  })

  const fieldRef = useRef<any>(null)

  const handleShowPasswordChecklist = (show: boolean) => {
    show
      ? enablePasswordChecklist && !shouldShowPasswordChecklist && setShouldShowPasswordChecklist(true)
      : hidePasswordChecklistOnBlur && setShouldShowPasswordChecklist(false)
  }

  const handleFocus = () => {
    if (isMobileSized && IS_WEB) {
      // scrollIntoView is only used for web and not native
      // native is taken care of by using KeyboardAwareScrollView in SetUpAccountForm
      fieldRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
    onFocus()
    handleShowPasswordChecklist(true)
  }

  const handleBlur = () => {
    onBlur()
    hidePasswordChecklistOnBlur ? handleShowPasswordChecklist(false) : null
  }

  const handleChange = (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
    const value = e.nativeEvent.text
    if (passwordPolicy) {
      setPasswordStrength({
        minUpperCase: isValidUpperCase(value, minUpperCase!),
        minLowerCase: isValidLowerCase(value, minLowerCase!),
        minNumber: isValidNumber(value, minNumber!),
        minLength: isValidLength(value, minLength!),
      })
    }
    onChange(value)
  }

  // this logic allows user to navigate to PasswordField from keyboard(mobile)
  useEffect(() => {
    if (setInputRefs && fieldRef && name) {
      setInputRefs({ name, ref: fieldRef })
    }
  }, [name, setInputRefs])

  // Native doesn't always blur the field when the user taps away, so also close the checklist
  // whenever the keyboard hides (e.g. the Android back button).
  useEffect(() => {
    if (IS_WEB || !hidePasswordChecklistOnBlur) {
      return undefined
    }
    const subscription = Keyboard.addListener('keyboardDidHide', () => setShouldShowPasswordChecklist(false))
    return () => subscription.remove()
  }, [hidePasswordChecklistOnBlur])

  return (
    <Container ref={fieldRef} elevateChecklist={elevateChecklist}>
      <InputField
        name={name}
        label={label}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        secureTextEntry={!showPassword}
        error={error}
        active={active}
        baseInputStyle={baseInputStyle}
        textContentType='password'
        /**
         * On Android: visible-password keyboard type causes autofill issues where it fails to hide
         * the password or shows an autofill bar inappropriately. Leaving as undefined on Android avoids this.
         *
         * On iOS: ascii-capable keyboard type is used instead of visible-password to prevent the keyboard
         * from switching back to alphabet layout after typing a number. The ascii-capable keyboard provides
         * a standard QWERTY keyboard that maintains consistent layout when switching between alphabetical
         * and numeric characters, which is the standard iOS password keyboard behavior.
         */
        keyboardType={Platform.OS === 'ios' ? 'ascii-capable' : undefined}
        importantForAutofill='yes'
        /**
         * letterSpacing does not seem to work on android and causing the text input to disappear
         * when being set to 1. There is no mention of letterSpacing working on either iOS or Android,
         * https://reactnative.dev/docs/textinput#style, however it does seem to behave correctly on iOS
         */
        typeStyle={{
          letterSpacing: !showPassword && Platform.OS !== 'android' ? 1 : 0,
          fontSize: 16,
          color: colors.textPrimary,
        }}
        inputContainerStyle={inputContainerStyle}
        labelContainerStyle={labelContainerStyle}
        autoComplete={autoComplete}
        inputAccessoryViewID={inputAccessoryViewID}
        areAllInputRefsSet={areAllInputRefsSet}
        returnKeyType={returnKeyType}
        getReturnKeyType={getReturnKeyType}
        navigateToNewField={navigateToNewField}
      >
        <Pressable
          accessible
          focusable
          importantForAccessibility='yes'
          tabIndex={0}
          accessibilityRole={AccessibilityRolesNative.BUTTON}
          testID={'PasswordField-showPasswordToggle'}
          onPress={togglePasswordVisibility}
          onKeyDown={handleKeyDown}
          onFocus={() => handleShowPasswordChecklist(true)}
          onBlur={() => (hidePasswordChecklistOnBlur ? handleBlur() : null)}
          accessibilityLabel={formatMessage(showPassword ? hidePasswordButtonLabel : showPasswordButtonLabel)}
        >
          <EyeIcon isOpen={showPassword} />
        </Pressable>
      </InputField>
      {shouldShowPasswordChecklist && passwordPolicy && (
        <PasswordChecklist
          passwordPolicy={passwordPolicy}
          passwordStrength={passwordStrength}
          customStyle={
            hidePasswordChecklistOnBlur
              ? {
                  position: 'absolute',
                  width: '100%',
                  top: Platform.OS === 'android' ? 84 : 80,
                  padding: 16,
                  backgroundColor: colors.backgroundDropdown,
                  border: `1px solid ${colors.borderDefault}`,
                  borderRadius: 8,
                  shadowColor: colors.outlineHigh,
                  shadowOpacity: '0.25',
                  shadowRadius: 8,
                  ...(elevateChecklist && { zIndex: 10 }),
                }
              : {}
          }
        />
      )}
    </Container>
  )
}

export const PasswordFieldRFF: FunctionComponent<FieldRenderProps<string>> = ({
  input: { onChange, name, onFocus, onBlur },
  meta: { error, active, touched, submitFailed },
  label,
  passwordPolicy,
  showPasswordChecklist = true,
  validateOnTouch = false,
  baseInputStyle,
  inputContainerStyle,
  labelContainerStyle,
  showPasswordChecklistByDefault,
  hidePasswordChecklistOnBlur,
  autoComplete,
  inputAccessoryViewID,
  elevateChecklist,
  handleOnInputFocus,
  setInputRefs,
  handleOnInputBlur,
  getReturnKeyType,
  returnKeyType,
  navigateToNewField,
  areAllInputRefsSet,
}) => {
  const { formatMessage } = useIntl()

  let errorValue = ''
  if ((validateOnTouch && touched && error) || (submitFailed && error)) {
    errorValue = isString(error) ? error : formatMessage(error)
  }

  return (
    <PasswordField
      name={name}
      label={label}
      error={errorValue}
      active={active}
      enablePasswordChecklist={showPasswordChecklist}
      passwordPolicy={passwordPolicy}
      onChange={onChange}
      onFocus={() => {
        if (handleOnInputFocus) {
          handleOnInputFocus(name)
        }
        onFocus()
      }}
      onBlur={() => {
        if (handleOnInputBlur) {
          handleOnInputBlur(name)
        }
        onBlur()
      }}
      baseInputStyle={baseInputStyle}
      inputContainerStyle={inputContainerStyle}
      labelContainerStyle={labelContainerStyle}
      showPasswordChecklistByDefault={showPasswordChecklistByDefault}
      hidePasswordChecklistOnBlur={hidePasswordChecklistOnBlur}
      autoComplete={autoComplete}
      inputAccessoryViewID={inputAccessoryViewID}
      elevateChecklist={elevateChecklist}
      setInputRefs={setInputRefs}
      returnKeyType={returnKeyType}
      areAllInputRefsSet={areAllInputRefsSet}
      getReturnKeyType={getReturnKeyType}
      navigateToNewField={navigateToNewField}
    />
  )
}
